/* =====================================================================================
   Safety: incidents, statutory reporting and the compliance register.

   Live-only: every read and write goes to Postgres, where the routing (incidents notify the
   owner), the RIDDOR deadline arithmetic and the row security live. The module renders empty
   states without a backend rather than inventing worked examples, because an invented
   accident book is exactly the kind of record that must never look real.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Incident {
  id: string; reference: string | null; kind: string; occurred_at: string;
  location: string; description: string;
  person_name: string | null; person_role: string | null; person_member_id: string | null;
  witnesses: string | null; injury_detail: string | null; immediate_action: string | null;
  confidential: boolean; severity: string; status: string;
  absence_started_on: string | null; returned_on: string | null; days_incapacitated: number | null;
  riddor_category: string | null; riddor_reportable: boolean; riddor_deadline: string | null;
  riddor_reported_on: string | null; riddor_reference: string | null;
  investigation: string | null; root_cause: string | null; actions: string | null;
  reported_by: string | null; created_at: string;
}

export interface ComplianceItem {
  id: string; code: string; title: string; statute: string | null; why: string | null;
  status: string; evidence: string | null; review_due: string | null; notes: string | null;
}

export const INCIDENT_KINDS = [
  ['injury', 'Injury'],
  ['ill_health', 'Work-related ill health'],
  ['near_miss', 'Near miss'],
  ['dangerous_occurrence', 'Dangerous occurrence'],
  ['violence', 'Violence or aggression'],
  ['environmental', 'Environmental'],
  ['property', 'Property or equipment'],
  ['wellbeing_concern', 'Wellbeing concern'],
] as const;

export const RIDDOR_CATEGORIES = [
  ['specified_injury', 'Specified injury (fracture, amputation, loss of sight, crush, burn, unconsciousness...)'],
  ['over_7_day', 'Over-7-day incapacitation'],
  ['occupational_disease', 'Occupational disease'],
  ['dangerous_occurrence', 'Dangerous occurrence'],
  ['gas_incident', 'Gas incident'],
  ['fatality', 'Death'],
] as const;

export async function fetchIncidents(): Promise<Incident[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('safety').from('incidents').select('*').order('occurred_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the incident register'));
  return (data ?? []) as Incident[];
}

export async function reportIncident(i: {
  kind: string; occurredAt: string; location: string; description: string;
  personName?: string; personRole?: string; witnesses?: string; injuryDetail?: string;
  immediateAction?: string; severity?: string; confidential?: boolean;
}): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('safety').rpc('report_incident', {
    p_kind: i.kind, p_occurred_at: i.occurredAt, p_location: i.location,
    p_description: i.description, p_person_name: i.personName || null,
    p_person_role: i.personRole || null, p_person_member: null,
    p_witnesses: i.witnesses || null, p_injury_detail: i.injuryDetail || null,
    p_immediate_action: i.immediateAction || null,
    p_severity: i.severity || 'minor', p_confidential: i.confidential ?? false,
  });
  if (error) throw new Error(explain(error.message, 'report the incident'));
  return data as string;
}

export const recordAbsence = async (incidentId: string, firstDay: string, returned?: string) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('safety').rpc('record_absence', {
    p_incident: incidentId, p_first_day: firstDay, p_returned: returned || null,
  });
  if (error) throw new Error(explain(error.message, 'record the absence'));
};

export const assessRiddor = async (incidentId: string, category: string | null, note?: string) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('safety').rpc('assess_riddor', {
    p_incident: incidentId, p_category: category, p_note: note || null,
  });
  if (error) throw new Error(explain(error.message, 'record the assessment'));
};

export const markReported = async (incidentId: string, reference: string) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('safety').rpc('mark_reported', {
    p_incident: incidentId, p_reference: reference,
  });
  if (error) throw new Error(explain(error.message, 'record the statutory report'));
};

export const updateIncident = async (id: string, patch: Record<string, unknown>) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('safety').from('incidents').update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the incident'));
};

export async function fetchCompliance(): Promise<ComplianceItem[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('safety').from('compliance_items').select('*').order('code');
  if (error) throw new Error(explain(error.message, 'read the compliance register'));
  return (data ?? []) as ComplianceItem[];
}

export const updateCompliance = async (id: string, patch: Record<string, unknown>) => {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('safety').from('compliance_items').update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the register'));
};

/** The organisation's home country decides which statutory panel shows (GBR: RIDDOR). */
export async function orgCountry(): Promise<string | null> {
  if (!live()) return null;
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs')
    .select('home_country').limit(1).maybeSingle();
  return (data as { home_country?: string } | null)?.home_country ?? null;
}

/** Whether this member decides safety matters (owner/admin/hr): shows the registers in full. */
export async function safetyGate(): Promise<boolean> {
  if (!live()) return false;
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').rpc('may_admin_access');
  return data === true;
}

export async function fetchSafetyPolicies(): Promise<{ id: string; doc_id: string; title: string;
  status: string; version: string | null; summary: string | null; file_path: string | null }[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').from('canon_documents')
    .select('id, doc_id, title, status, version, summary, file_path')
    .eq('category', 'safety').eq('is_current', true).order('doc_id');
  if (error) throw new Error(explain(error.message, 'read the policies'));
  return (data ?? []) as never;
}
